import { SUPPLIER_BACKEND_API_ENDPOINTS } from "../config/apiConfig";
import { apiClient } from "./api-client.service";

import type {
  CheckAvailability,
  AgentDetailResponse,
  AgentFilter,
  AgentMutationResponse,
  AgentPayload,
  AgentResponse,
} from "../types/agents.types";

const endpoints = SUPPLIER_BACKEND_API_ENDPOINTS.agents;

const failure = (error: unknown, fallback: string) => ({
  success: false,
  statusCode: 500,
  message: error instanceof Error ? error.message : fallback,
  resData: null,
});

export const agentsService = {
  async list(filter: AgentFilter): Promise<AgentResponse> {
    try {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      });

      return await apiClient<AgentResponse>(endpoints.list(params.toString()), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(
        error,
        "Failed to load agents due to an unexpected error.",
      ) as unknown as AgentResponse;
    }
  },

  async details(id: number | string): Promise<AgentDetailResponse> {
    try {
      return await apiClient<AgentDetailResponse>(endpoints.details(id), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(
        error,
        "Failed to load agent details due to an unexpected error.",
      ) as unknown as AgentDetailResponse;
    }
  },

  async detailsByCode(agentCode: string): Promise<AgentDetailResponse> {
    try {
      return await apiClient<AgentDetailResponse>(
        endpoints.agentByCode(encodeURIComponent(agentCode)),
        {
          method: "GET",
          isAuthenticated: true,
          cache: "no-store",
        },
      );
    } catch (error) {
      return failure(
        error,
        "Failed to load agent details due to an unexpected error.",
      ) as unknown as AgentDetailResponse;
    }
  },

  async create(payload: AgentPayload): Promise<AgentMutationResponse> {
    try {
      return await apiClient<AgentMutationResponse>(endpoints.create, {
        method: "POST",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to create agent due to an unexpected error.",
      ) as AgentMutationResponse;
    }
  },

  async update(
    id: number | string,
    payload: Partial<AgentPayload>,
  ): Promise<AgentMutationResponse> {
    try {
      return await apiClient<AgentMutationResponse>(endpoints.update(id), {
        method: "PATCH",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to update agent due to an unexpected error.",
      ) as AgentMutationResponse;
    }
  },

  async updateStatus(
    id: number | string,
    payload: { status: "active" | "inactive" },
  ): Promise<AgentMutationResponse> {
    return agentsService.update(id, payload);
  },

  async checkAvailability(
    filter: Partial<CheckAvailability>,
  ): Promise<AgentMutationResponse> {
    try {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value) params.set(key, String(value));
      });

      return await apiClient<AgentMutationResponse>(
        endpoints.checkAvailability(params.toString()),
        {
          method: "GET",
          isAuthenticated: true,
          cache: "no-store",
        },
      );
    } catch (error) {
      return failure(
        error,
        "Failed to check availability due to an unexpected error.",
      ) as AgentMutationResponse;
    }
  },

  async delete(id: number | string): Promise<AgentMutationResponse> {
    try {
      return await apiClient<AgentMutationResponse>(endpoints.delete(id), {
        method: "DELETE",
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to delete agent due to an unexpected error.",
      ) as AgentMutationResponse;
    }
  },
};
